import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { alpha, Box, Button, Menu, MenuItem, Typography, useTheme } from '@mui/material';
import { pushMessage } from 'redux/reducers/messages';
import { getUserPermissions, updateUserData, userSelector } from 'redux/reducers/user';
import { useAppDispatch } from 'redux/store';
import { mapOptionIdToPrimarySportValue, PRIMARY_SPORT_OPTIONS } from 'utils/primarySport';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';

type PrimarySportQuickSelectProps = {
    compact?: boolean;
};

const PrimarySportQuickSelect = ({ compact = false }: PrimarySportQuickSelectProps) => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { userData } = useSelector(userSelector);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [isSaving, setIsSaving] = useState(false);

    const open = Boolean(anchorEl);

    const handleSave = async (optionId: (typeof PRIMARY_SPORT_OPTIONS)[number]['id']) => {
        if (!userData?.uuid) return;
        setIsSaving(true);
        setAnchorEl(null);

        const selectedValue = mapOptionIdToPrimarySportValue(optionId);
        const result = await dispatch(
            updateUserData({
                userUuid: userData.uuid,
                first_name: null,
                last_name: null,
                email: null,
                user_name: null,
                phone: null,
                profile_photo: null,
                primary_sport: selectedValue,
            }),
        );

        setIsSaving(false);

        if (updateUserData.fulfilled.match(result)) {
            dispatch(getUserPermissions({ id: userData.uuid }));
            dispatch(
                pushMessage({
                    status: 'success',
                    message: 'Primary sport updated successfully.',
                }),
            );
            return;
        }

        dispatch(
            pushMessage({
                status: 'error',
                message: 'Failed to update primary sport, please try again.',
            }),
        );
    };

    return (
        <Box sx={{ mr: compact ? 0.5 : 2 }}>
            <Button
                size={compact ? 'small' : 'medium'}
                variant="contained"
                color="warning"
                onClick={(event) => setAnchorEl(event.currentTarget)}
                endIcon={<KeyboardArrowDownIcon />}
                disabled={isSaving}
                sx={{
                    fontWeight: 800,
                    letterSpacing: 0.2,
                    textTransform: 'uppercase',
                    borderRadius: compact ? 2 : 2.5,
                    px: compact ? 1.25 : 2.25,
                    py: compact ? 0.5 : 0.9,
                    backdropFilter: 'blur(16px) saturate(170%)',
                    WebkitBackdropFilter: 'blur(16px) saturate(170%)',
                    boxShadow: `0 8px 20px ${alpha(theme.palette.common.black, 0.25)}`,
                }}
            >
                {compact ? 'Sport' : 'Set Primary Sport'}
            </Button>
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={() => setAnchorEl(null)}
                PaperProps={{
                    sx: {
                        minWidth: compact ? 210 : 260,
                        mt: 1,
                        borderRadius: 2,
                        background: `linear-gradient(135deg, ${alpha(
                            theme.palette.background.paper,
                            0.82,
                        )} 0%, ${alpha(theme.palette.background.paper, 0.68)} 100%)`,
                        backdropFilter: 'blur(20px) saturate(170%)',
                        WebkitBackdropFilter: 'blur(20px) saturate(170%)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.22)}`,
                        boxShadow: `0 12px 32px ${alpha(theme.palette.common.black, 0.18)}`,
                    },
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ px: 2, pt: 1.5, pb: 1, display: 'block', color: 'text.secondary' }}
                >
                    Select your primary sport
                </Typography>
                {PRIMARY_SPORT_OPTIONS.map((option) => (
                    <MenuItem key={option.id} onClick={() => handleSave(option.id)}>
                        {option.label}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    );
};

export default PrimarySportQuickSelect;
